import React from "react";
import { IconButton, Tooltip } from "@mui/material";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import StatusPill from "../../common/StatusPill";
import useMissionStore, { isMissionActive } from "./useMissionStore";
import SessionMissionDialog from "./SessionMissionDialog";
import { missionStatusVariant, statusLabel } from "./missionDisplay";
import "./Missions.css";

/**
 * Chat-header entry point for the harness layer: turn the current agent session into a mission,
 * then pause / resume / stop it. Renders nothing until the session's mission state is known.
 */
function MissionControl({ session }) {
  const chatSessionId = session?.chatSessionId;
  const mission = useMissionStore(
    (s) => s.missionBySession[chatSessionId] ?? null
  );
  const fetchMission = useMissionStore((s) => s.fetchMission);
  const startMission = useMissionStore((s) => s.startMission);
  const pauseMission = useMissionStore((s) => s.pauseMission);
  const resumeMission = useMissionStore((s) => s.resumeMission);
  const stopMission = useMissionStore((s) => s.stopMission);
  const error = useMissionStore((s) => s.errorBySession[chatSessionId] ?? null);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (chatSessionId) fetchMission(chatSessionId);
  }, [chatSessionId, fetchMission]);

  if (!chatSessionId) return null;

  const active = isMissionActive(mission);
  const isPaused = mission?.status === "PAUSED";

  const handleStart = async (payload) => {
    setBusy(true);
    const started = await startMission(chatSessionId, payload);
    setBusy(false);
    if (started) setDialogOpen(false);
  };

  const runLifecycle = async (fn) => {
    setBusy(true);
    await fn(chatSessionId, mission.missionId);
    setBusy(false);
  };

  if (active) {
    return (
      <div className="mission-control mission-control--active">
        <StatusPill
          variant={missionStatusVariant(mission.status)}
          size="sm"
          title="Mission status"
        >
          Mission · {statusLabel(mission.status)}
        </StatusPill>
        {isPaused ? (
          <Tooltip title="Resume mission" arrow>
            <span>
              <IconButton
                size="small"
                disabled={busy}
                onClick={() => runLifecycle(resumeMission)}
              >
                <PlayArrowRoundedIcon className="mission-icon" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Pause mission" arrow>
            <span>
              <IconButton
                size="small"
                disabled={busy}
                onClick={() => runLifecycle(pauseMission)}
              >
                <PauseRoundedIcon className="mission-icon" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Stop mission" arrow>
          <span>
            <IconButton
              size="small"
              disabled={busy}
              onClick={() => runLifecycle(stopMission)}
            >
              <StopRoundedIcon className="mission-icon" />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="mission-control">
      <Tooltip title="Run this session as a mission" arrow>
        <button
          type="button"
          className="mission-attach-btn"
          onClick={() => setDialogOpen(true)}
        >
          <FlagOutlinedIcon className="mission-attach-btn__icon" />
          Make this a mission
        </button>
      </Tooltip>
      <SessionMissionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onStart={handleStart}
        busy={busy}
        error={error}
      />
    </div>
  );
}

export default MissionControl;
